import React, { useState, useCallback, useRef, useEffect } from 'react';
import { graphql, useFragment } from 'react-relay';
import { Box, xcss } from '@atlaskit/primitives';
import { fg } from '@atlassian/jira-feature-gating';
import JQLBuilder from '@atlassian/jira-jql-builder/src/main.tsx';
import { useJqlDebuggerStoreActions } from '@atlassian/jira-jql-debugger/src/controllers/jql-debugger-data/index.tsx';
import { getWillShowNav4 } from '@atlassian/jira-navigation-apps-sidebar-nav4-rollout-core/src/common/utils/get-will-show-nav4/index.tsx';
import { ContextualAnalyticsData, SCREEN } from '@atlassian/jira-product-analytics-bridge';
import type { ui_jqlBuilderWithAi_JQLBuilderWithAI$key as FragmentKey } from '@atlassian/jira-relay/src/__generated__/ui_jqlBuilderWithAi_JQLBuilderWithAI.graphql';
import { expVal } from '@atlassian/jira-feature-experiments';
import type { JQLBuilderWithAIProps } from '../common/types.tsx';
import { useNaturalLanguageMode } from '../controllers/natural-language-mode/index.tsx';
import { AIPremiumUpsellButtonPopup } from './ai-premium-upsell-button-popup/index.tsx';
import { NaturalLanguageToJQLWrapper } from './natural-language-to-jql-wrapper/index.tsx';
import { NaturalLanguageToggle } from './natural-language-toggle/index.tsx';
import { SpotlightTourContainer } from './spotlight-tour-container/index.tsx';

const JQLBuilderWithAI = ({
	onToggleAi,
	fragmentKey,
	maybeHydrationQueryReference,
	...props
}: JQLBuilderWithAIProps) => {
	const data = useFragment<FragmentKey>(
		graphql`
			fragment ui_jqlBuilderWithAi_JQLBuilderWithAI on JiraQuery {
				shouldShowAtlassianIntelligence(
					cloudId: $cloudId
					atlassianIntelligenceProductFeatureInput: {
						feature: $atlassianIntelligenceProductFeature
					}
				) @optIn(to: "JiraNaturalLanguageToJQL")
				...naturalLanguageToggle_jqlBuilderWithAi
				userPreferences(cloudId: $cloudId) @optIn(to: "JiraUserPreferences") {
					...spotlightTourContainer_jqlBuilderWithAi
					...naturalLanguageToJqlWrapper_jqlBuilderWithAi
					...main_jqlBuilder_JQLBuilder
				}
			}
		`,
		fragmentKey,
	);
	const isAtlassianIntelligenceEnabled = !!data?.shouldShowAtlassianIntelligence;
	const userPreferencesData = data?.userPreferences || null;

	// when cleaning up getWillShowNav4() remove useState(...) and rename isNLMode to isNaturalLanguageMode
	const [isNaturalLanguageMode, setIsNaturalLanguageMode] = useState(false);

	const [isNLMode, { toggleNaturalLanguageMode, exitNaturalLanguageMode }] =
		useNaturalLanguageMode();

	const jqlDebuggerStoreActions = expVal(
		'gravityai-1783_jql_debugger',
		'jql_debugger_enabled',
		false,
	)
		? // eslint-disable-next-line react-hooks/react-compiler
			// eslint-disable-next-line react-hooks/rules-of-hooks
			useJqlDebuggerStoreActions()
		: undefined;

	if (expVal('gravityai-1783_jql_debugger', 'jql_debugger_enabled', false)) {
		jqlDebuggerStoreActions?.setExitNaturalLanguageMode(exitNaturalLanguageMode);
	}

	const autoFocusNLToggle = useRef(false);

	const onToggleNaturalLanguageMode = useCallback(() => {
		if (!getWillShowNav4()) {
			onToggleAi && onToggleAi(!isNaturalLanguageMode);
			setIsNaturalLanguageMode(!isNaturalLanguageMode);

			if (expVal('gravityai-1783_jql_debugger', 'jql_debugger_enabled', false)) {
				// When the user enters the NL mode, we want to reset the JQL debugger so that two AI experiences are not live simultaneously
				// Current isNaturalLanguageMode is false -> means that onToggleNaturalLanguageMode was called to enter the NaturalLanguage Mode
				if (!isNaturalLanguageMode) {
					jqlDebuggerStoreActions?.resetStatus('ON_ENTER_NL_MODE');
				}
			}
		} else {
			onToggleAi && onToggleAi(!isNLMode);
			toggleNaturalLanguageMode();

			if (expVal('gravityai-1783_jql_debugger', 'jql_debugger_enabled', false)) {
				// When the user enters the NL mode, we want to reset the JQL debugger so that two AI experiences are not live simultaneously
				// Current isNLMode is false -> means that onToggleNaturalLanguageMode was called to enter the NaturalLanguage Mode
				if (!isNLMode) {
					jqlDebuggerStoreActions?.resetStatus('ON_ENTER_NL_MODE');
				}
			}
		}

		// Return focus to the toggle button when exiting NL mode
		autoFocusNLToggle.current = true;
	}, [
		isNLMode,
		isNaturalLanguageMode,
		onToggleAi,
		toggleNaturalLanguageMode,
		jqlDebuggerStoreActions,
	]);

	useEffect(() => {
		autoFocusNLToggle.current = false;
	});

	// Delegate to JQLBuilder if NL is not enabled
	if (!isAtlassianIntelligenceEnabled) {
		return fg('nin_request_hydrate_query_earlier') ? (
			<JQLBuilder
				{...props}
				userPreferencesData={userPreferencesData}
				beforeControls={<AIPremiumUpsellButtonPopup />}
				maybeHydrationQueryReference={maybeHydrationQueryReference}
			/>
		) : (
			<JQLBuilder
				{...props}
				userPreferencesData={userPreferencesData}
				beforeControls={<AIPremiumUpsellButtonPopup />}
			/>
		);
	}

	const JQLBuilderWithAIComponent = fg('nin_request_hydrate_query_earlier') ? (
		<JQLBuilder
			{...props}
			userPreferencesData={userPreferencesData}
			maybeHydrationQueryReference={maybeHydrationQueryReference}
			beforeControls={
				<NaturalLanguageToggle
					autoFocus={autoFocusNLToggle.current}
					onClick={onToggleNaturalLanguageMode}
					fragmentRef={data}
				/>
			}
		/>
	) : (
		<JQLBuilder
			{...props}
			userPreferencesData={userPreferencesData}
			beforeControls={
				<NaturalLanguageToggle
					autoFocus={autoFocusNLToggle.current}
					onClick={onToggleNaturalLanguageMode}
					fragmentRef={data}
				/>
			}
		/>
	);

	return (
		<ContextualAnalyticsData
			sourceName="jqlBuilderWithAi"
			sourceType={SCREEN}
			attributes={{
				isAiAvailable: isAtlassianIntelligenceEnabled,
				isAiEnabled: getWillShowNav4() ? isNLMode : isNaturalLanguageMode,
			}}
		>
			<SpotlightTourContainer userPreferences={userPreferencesData}>
				<Box
					xcss={jqlBuilderWrapperStyles}
					// eslint-disable-next-line react/jsx-props-no-spreading, no-nested-ternary
					{...(fg('render_jql_builder_in_ssr')
						? __SERVER__
							? { 'data-ssr-placeholder': 'jql-builder-with-ai' }
							: { 'data-ssr-placeholder-replace': 'jql-builder-with-ai' }
						: {})}
					data-vc={`jql-builder-with-ai${__SERVER__ ? '-ssr' : ''}`}
				>
					{(getWillShowNav4() && isNLMode) || isNaturalLanguageMode ? (
						<NaturalLanguageToJQLWrapper
							{...props}
							onBack={onToggleNaturalLanguageMode}
							userPreferences={userPreferencesData}
						/>
					) : (
						JQLBuilderWithAIComponent
					)}
				</Box>
			</SpotlightTourContainer>
		</ContextualAnalyticsData>
	);
};

export default JQLBuilderWithAI;

const jqlBuilderWrapperStyles = xcss({
	// JQL editor height is 52px for both "Basic" and "JQL" views
	// This can cause a layout shift when switching to the AI searchbox
	// Prior to using a min height on the common parent, the approach taken to avoid the layout shift is by using a matching margin
	// Using the min-height on the common parent is more immune for future changes in the AI search field effective height
	minHeight: '52px',
});
